import { routeInfo } from '../../routeInfo';
import { put, call, takeLatest, takeEvery, all } from 'redux-saga/effects';
import { push } from 'react-router-redux';
import * as types from './actionTypes.diffMode';
import * as errorSaga from '../errors/saga.errors';
import * as projectsSaga from '../projects/saga.projects';
import * as _ from 'lodash';
import Api from '../../api/Api';
import { IBaseAction } from '../base';
import { isCiCdTag } from '../../services/mini/isCiCdTag';
import { Environment } from '../../const/environment';
import { getTransformFromText } from '../../views/pages/project/TransformMode/getTransformFromText';
import { getTransformTextRepresentation } from '../../views/pages/project/TransformMode/getTransformTextRepresentation';

export interface IOpenDiffModeParams {
    projectName: string, scriptName: string
}

export function* openDiffMode({ projectName, scriptName }: IOpenDiffModeParams) {
    try {
        yield put({ type: types.OPEN_DIFF_MODE_OK });
        yield put(push(routeInfo.diff.url(projectName, scriptName)));

    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export interface IFillDiffModeParams {
    projectId: string, environment: Environment
}

export function* fillDiffMode({ projectId, environment }: IFillDiffModeParams) {
    try {
        const project = yield projectsSaga.getProjectDataForComparingVersions({ projectId, environment });
        const tags = project?.tags?.filter((tag) => {
            return !isCiCdTag(tag);
        }) || [];
        const sortedTags = _.sortBy(tags.map(el => ({ ...el, ...{ createTs: +el.createTs } })), 'createTs');
        const newestTag = sortedTags[sortedTags.length - 1];
        const oldestTag = sortedTags[0];
        const fromTagId = newestTag.tagId;
        const toTagId = oldestTag.tagId;
        yield compareVersions({ projectId, fromTagId, toTagId });

    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export interface ICompareVersionsParams {
    projectId: string, fromTagId: string, toTagId: string
}
export function* compareVersions({ projectId, fromTagId, toTagId }: ICompareVersionsParams) {
    try {
        yield put({ type: types.CHANGE_VERSION_TAG_REQ, fromTagId, toTagId });

        const {
            scripts: initialScripts,
        } = yield call(Api.getScripts, projectId, fromTagId);
        const {
            scripts: newScripts,
        } = yield call(Api.getScripts, projectId, toTagId);

        const allScripts = {};
        let scriptIndexToOpen = 0;

        for (let i = 0; i < newScripts.length; i++) {
            const script = newScripts[i];

            allScripts[script.name] = {
                name: script.name,
                scriptId: script.scriptId,
                initialText: '',
                newText: script.text,
                deleted: false,
                updated: false,
                added: true,
                type: script.type
            };
        }

        for (let i = 0; i < initialScripts.length; i++) {
            const script = initialScripts[i];
            if (allScripts[script.name]) {
                allScripts[script.name].initialText = script.text;
                allScripts[script.name].updated = script.text !== allScripts[script.name].newText;
                allScripts[script.name].added = false;
                if (script.type === 'transform') {
                    allScripts[script.name].transformTextRepresentation = getTransformTextRepresentation(getTransformFromText(script.text));
                    allScripts[script.name].newTransformTextRepresentation = getTransformTextRepresentation(getTransformFromText(allScripts[script.name].newText));
                }
            } else {
                allScripts[script.name] = {
                    name: script.name,
                    scriptId: script.scriptId,
                    initialText: script.text,
                    newText: '',
                    deleted: true,
                    updated: false,
                    added: false,
                    type: script.type
                };
            }
        }



        const allScriptsArr = Object.keys(allScripts).map(function (key) {
            return allScripts[key];
        }).filter(script => {
            return script.added || script.deleted || script.updated;
        });

        for (let i = 0; i < allScriptsArr.length; i++) {
            const curScript = allScriptsArr[i];
            if (curScript.updated || curScript.added) {
                scriptIndexToOpen = i;
                break;
            }
        }

        yield put({ type: types.COMPARE_VERSIONS_OK, fromTagId, toTagId, scripts: allScriptsArr });
        if (allScriptsArr.length > 0) {
            yield put({ type: types.CHANGE_SELECTED_SCRIPT_REQ, script: allScriptsArr[scriptIndexToOpen] });
        }

    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* watch() {
    yield takeEvery<IOpenDiffModeParams & IBaseAction>(types.OPEN_DIFF_MODE_REQ, openDiffMode);
    yield takeEvery<IFillDiffModeParams & IBaseAction>(types.FILL_DIFF_MODE_REQ, fillDiffMode);
    yield takeEvery<ICompareVersionsParams & IBaseAction>(types.COMPARE_VERSIONS_REQ, compareVersions);
}
